// The card definition: built-in themes, pattern tiles, image text tones and the
// scrim rule. One definition, three registered hand-copies (this module, the
// admin copy, the backend email token table), see the strict rule in CLAUDE.md.

export const GIFT_CARD_ASPECT = 1.586;

export const GIFT_CARD_PATTERNS = [
  'arcs',
  'diagonal-lines',
  'leaves',
  'confetti',
  'none',
] as const;

export type GiftCardPattern = (typeof GIFT_CARD_PATTERNS)[number];

// The first five are retired from the admin's picker but stay valid, so the
// cards made with them keep drawing (D291).
export const GIFT_CARD_THEME_KEYS = [
  'classic',
  'midnight',
  'botanical',
  'celebration',
  'minimal',
  'silk',
  'black_gold',
  'golden_hour',
  'terracotta',
  'green_rings',
  'green_cairn',
  'mint_scatter',
  'green_tube',
] as const;

export type GiftCardThemeKey = (typeof GIFT_CARD_THEME_KEYS)[number];

export type GiftCardTextTone = 'LIGHT' | 'DARK';

export type GiftCardTheme = {
  key: GiftCardThemeKey;
  name: string;
  // null means the background is the studio's brand color, read from the
  // appearance branding at render and frozen by the server at purchase.
  bg: string | null;
  text: string;
  text_muted: string;
  pattern: GiftCardPattern;
  pattern_opacity: number;
  border?: string;
  // Only a theme without a fixed bg carries one: its text tone is fixed and a
  // scrim behind the text zones is what holds AA on any brand hex.
  scrim?: GiftCardTextTone;
  // A built-in background under public/, its glass and tint baked in until
  // every text zone holds AA, so it takes no scrim. bg is the color it
  // averages to, painted under it and used as the gift email's block (D291).
  image?: string;
};

export const GIFT_CARD_THEMES: Record<GiftCardThemeKey, GiftCardTheme> = {
  classic: {
    key: 'classic',
    name: 'Classic',
    bg: null,
    text: '#FFFFFF',
    text_muted: '#E4E7EC',
    pattern: 'arcs',
    pattern_opacity: 0.16,
    scrim: 'LIGHT',
  },
  midnight: {
    key: 'midnight',
    name: 'Midnight',
    bg: '#131A2C',
    text: '#FFFFFF',
    text_muted: '#AEB5C6',
    pattern: 'diagonal-lines',
    pattern_opacity: 0.12,
  },
  botanical: {
    key: 'botanical',
    name: 'Botanical',
    bg: '#24463B',
    text: '#F4F7F3',
    text_muted: '#BFD4C8',
    pattern: 'leaves',
    pattern_opacity: 0.14,
  },
  celebration: {
    key: 'celebration',
    name: 'Celebration',
    bg: '#F6E7D4',
    text: '#2B2118',
    text_muted: '#6B5646',
    pattern: 'confetti',
    pattern_opacity: 0.9,
  },
  minimal: {
    key: 'minimal',
    name: 'Minimal',
    bg: '#F5F3EC',
    text: '#181D27',
    text_muted: '#5F636B',
    pattern: 'none',
    pattern_opacity: 0,
    border: '#D9D4C7',
  },
  silk: {
    key: 'silk',
    name: 'Silk',
    bg: '#260608',
    text: '#FFFFFF',
    text_muted: '#E4E7EC',
    pattern: 'none',
    pattern_opacity: 0,
    image: '/images/gift-card-themes/silk.webp',
  },
  black_gold: {
    key: 'black_gold',
    name: 'Black gold',
    bg: '#111010',
    text: '#FFFFFF',
    text_muted: '#E4E7EC',
    pattern: 'none',
    pattern_opacity: 0,
    image: '/images/gift-card-themes/black-gold.webp',
  },
  golden_hour: {
    key: 'golden_hour',
    name: 'Golden hour',
    bg: '#CFA46E',
    text: '#181D27',
    text_muted: '#3A4050',
    pattern: 'none',
    pattern_opacity: 0,
    image: '/images/gift-card-themes/golden-hour.webp',
  },
  terracotta: {
    key: 'terracotta',
    name: 'Terracotta',
    bg: '#914927',
    text: '#FFFFFF',
    text_muted: '#E4E7EC',
    pattern: 'none',
    pattern_opacity: 0,
    image: '/images/gift-card-themes/terracotta.webp',
  },
  green_rings: {
    key: 'green_rings',
    name: 'Green rings',
    bg: '#1E4630',
    text: '#FFFFFF',
    text_muted: '#E4E7EC',
    pattern: 'none',
    pattern_opacity: 0,
    image: '/images/gift-card-themes/green-rings.webp',
  },
  green_cairn: {
    key: 'green_cairn',
    name: 'Green cairn',
    bg: '#1F442D',
    text: '#FFFFFF',
    text_muted: '#E4E7EC',
    pattern: 'none',
    pattern_opacity: 0,
    image: '/images/gift-card-themes/green-cairn.webp',
  },
  mint_scatter: {
    key: 'mint_scatter',
    name: 'Mint scatter',
    bg: '#C6E1CD',
    text: '#181D27',
    text_muted: '#3A4050',
    pattern: 'none',
    pattern_opacity: 0,
    image: '/images/gift-card-themes/mint-scatter.webp',
  },
  green_tube: {
    key: 'green_tube',
    name: 'Green tube',
    bg: '#244D37',
    text: '#FFFFFF',
    text_muted: '#E4E7EC',
    pattern: 'none',
    pattern_opacity: 0,
    image: '/images/gift-card-themes/green-tube.webp',
  },
};

export type GiftCardToneStyle = {
  text: string;
  text_muted: string;
  // What the card paints when the image fails to load, so the text stays
  // readable on a solid block of the same tone.
  fallback_bg: string;
};

export const GIFT_CARD_TONES: Record<GiftCardTextTone, GiftCardToneStyle> = {
  LIGHT: { text: '#FFFFFF', text_muted: '#E4E7EC', fallback_bg: '#1F2633' },
  DARK: { text: '#181D27', text_muted: '#3A4050', fallback_bg: '#ECE8DF' },
};

// Text zones as a share of the card height from the top edge, and the least
// scrim alpha each needs. The gradients below are shaped so every stop inside
// a zone is at or above its floor; the token-schema test checks both the
// stops and the worst-case contrast (a white or black photo behind them).
export const GIFT_CARD_TEXT_ZONES = {
  studio: { from: 0.08, to: 0.2, min_scrim: 0.58, large: false },
  headline: { from: 0.54, to: 0.66, min_scrim: 0.48, large: true },
  code: { from: 0.7, to: 0.78, min_scrim: 0.62, large: false },
  footer: { from: 0.84, to: 0.94, min_scrim: 0.72, large: false },
} as const;

type ScrimStop = { at: number; alpha: number };

// Position is the share of the card height from the top edge.
export const GIFT_CARD_SCRIM_STOPS: { top: ScrimStop[]; bottom: ScrimStop[] } =
  {
    top: [
      { at: 0, alpha: 0.62 },
      { at: 0.22, alpha: 0.6 },
      { at: 0.4, alpha: 0 },
    ],
    bottom: [
      { at: 0.36, alpha: 0 },
      { at: 0.54, alpha: 0.5 },
      { at: 0.7, alpha: 0.64 },
      { at: 0.84, alpha: 0.72 },
      { at: 1, alpha: 0.74 },
    ],
  };

const SCRIM_RGB: Record<GiftCardTextTone, string> = {
  LIGHT: '0, 0, 0',
  DARK: '255, 255, 255',
};

const gradient = (rgb: string, stops: ScrimStop[]): string =>
  `linear-gradient(to bottom, ${stops
    .map(({ at, alpha }) => `rgba(${rgb}, ${alpha}) ${at * 100}%`)
    .join(', ')})`;

export const scrimBackground = (tone: GiftCardTextTone): string => {
  const rgb = SCRIM_RGB[tone];
  return [
    gradient(rgb, GIFT_CARD_SCRIM_STOPS.top),
    gradient(rgb, GIFT_CARD_SCRIM_STOPS.bottom),
  ].join(', ');
};

export const scrimAlphaAt = (position: number): number => {
  const pick = (stops: ScrimStop[]) => {
    if (position <= stops[0].at) return stops[0].alpha;
    const last = stops[stops.length - 1];
    if (position >= last.at) return last.alpha;
    const next = stops.findIndex((stop) => stop.at >= position);
    const a = stops[next - 1];
    const b = stops[next];
    return a.alpha + ((position - a.at) / (b.at - a.at)) * (b.alpha - a.alpha);
  };
  return Math.max(
    pick(GIFT_CARD_SCRIM_STOPS.top),
    pick(GIFT_CARD_SCRIM_STOPS.bottom)
  );
};

const CONFETTI_COLORS = ['#F97066', '#2FB89A', '#F2B84B', '#8E7CF5'];

const PATTERN_TILES: Record<
  Exclude<GiftCardPattern, 'none'>,
  (color: string) => { size: number; body: string }
> = {
  arcs: (color) => ({
    size: 48,
    body: [
      `<g fill="none" stroke="${color}" stroke-width="1.5">`,
      '<path d="M0 48a48 48 0 0 1 48-48"/>',
      '<path d="M0 48a36 36 0 0 1 36-36"/>',
      '<path d="M0 48a24 24 0 0 1 24-24"/>',
      '<path d="M0 48a12 12 0 0 1 12-12"/>',
      '</g>',
    ].join(''),
  }),
  'diagonal-lines': (color) => ({
    size: 12,
    body: `<path d="M-1 13L13-1M-1 1L1-1M11 13l2-2" stroke="${color}" stroke-width="1"/>`,
  }),
  leaves: (color) => ({
    size: 56,
    body: [
      `<g fill="none" stroke="${color}" stroke-width="1.4" stroke-linecap="round">`,
      '<path d="M8 40c0-12 8-22 20-24-2 12-9 22-20 24z"/>',
      '<path d="M8 40l14-16"/>',
      '<path d="M48 16c0 12-8 22-20 24 2-12 9-22 20-24z"/>',
      '<path d="M48 16L34 32"/>',
      '</g>',
    ].join(''),
  }),
  confetti: () => ({
    size: 72,
    body: [
      `<rect x="8" y="10" width="8" height="4" rx="1" fill="${CONFETTI_COLORS[0]}" transform="rotate(-24 12 12)"/>`,
      `<circle cx="40" cy="8" r="2.6" fill="${CONFETTI_COLORS[1]}"/>`,
      `<rect x="54" y="20" width="8" height="4" rx="1" fill="${CONFETTI_COLORS[2]}" transform="rotate(30 58 22)"/>`,
      `<circle cx="20" cy="36" r="2.2" fill="${CONFETTI_COLORS[3]}"/>`,
      `<rect x="34" y="42" width="8" height="4" rx="1" fill="${CONFETTI_COLORS[1]}" transform="rotate(-50 38 44)"/>`,
      `<circle cx="62" cy="50" r="2.6" fill="${CONFETTI_COLORS[0]}"/>`,
      `<rect x="10" y="58" width="8" height="4" rx="1" fill="${CONFETTI_COLORS[2]}" transform="rotate(18 14 60)"/>`,
      `<circle cx="46" cy="64" r="2.2" fill="${CONFETTI_COLORS[3]}"/>`,
    ].join(''),
  }),
};

// Tiny SVG tiles generated in code and painted as a background data URI. No
// image files for built-ins; the email copy drops the pattern entirely.
export const patternTile = (
  pattern: GiftCardPattern,
  color: string,
  opacity: number
): { image: string; size: number } | null => {
  if (pattern === 'none') return null;
  const { size, body } = PATTERN_TILES[pattern](color);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><g opacity="${opacity}">${body}</g></svg>`;
  return {
    image: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
    size,
  };
};

// The backend's public shape: a built-in theme, or one uploaded image with the
// text tone the admin picked for it.
export type GiftCardLook =
  | { type: 'theme'; theme_key: GiftCardThemeKey }
  | { type: 'image'; card_image_url: string; text_tone: GiftCardTextTone };

export type GiftCardStyle = {
  bg: string;
  text: string;
  text_muted: string;
  border: string | null;
  pattern: { image: string; size: number } | null;
  // The uploaded image and the scrim behind its text zones; null for a solid
  // theme.
  image: string | null;
  scrim: GiftCardTextTone | null;
};

// brandColor is the studio's branding button color; only Classic paints it.
export const resolveGiftCardStyle = (
  look: GiftCardLook,
  brandColor: string
): GiftCardStyle => {
  if (look.type === 'image') {
    const tone = GIFT_CARD_TONES[look.text_tone];
    return {
      bg: tone.fallback_bg,
      text: tone.text,
      text_muted: tone.text_muted,
      border: null,
      pattern: null,
      image: look.card_image_url,
      scrim: look.text_tone,
    };
  }
  const theme = GIFT_CARD_THEMES[look.theme_key];
  return {
    bg: theme.bg ?? brandColor,
    text: theme.text,
    text_muted: theme.text_muted,
    border: theme.border ?? null,
    pattern: patternTile(theme.pattern, theme.text, theme.pattern_opacity),
    image: theme.image ?? null,
    scrim: theme.scrim ?? null,
  };
};
